// M12 — OTP allowlist admin screen. Unlike the catalogue screens, this
// tab has no draft/publish flow: every action (approve/reject/add/bulk)
// commits server-side immediately and the server pushes back the fresh
// `accessRequestList` so the table re-renders.

import { el } from "./dom.js";

export interface AccessRow {
  email: string;
  status: "pending" | "approved";
  source: "self_request" | "admin_added";
  firstRequestAt: number;
  lastRequestAt: number;
  requestCount: number;
  ip: string | null;
  approvedAt: number | null;
  approvedBy: string | null;
}

export interface AccessScreenCallbacks {
  onAdd: (email: string) => void;
  onApprove: (email: string) => void;
  onReject: (email: string) => void;
  onBulkRejectByIp: (ip: string) => void;
  onRefresh: (filter: { status?: "pending" | "approved"; ip?: string }) => void;
}

export interface AccessScreen {
  root: HTMLElement;
  setRows(rows: AccessRow[]): void;
  setError(reason: string | null): void;
}

type StatusFilter = "all" | "pending" | "approved";

function formatRelative(ms: number): string {
  if (ms <= 0) return "just now";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function createAccessScreen(cb: AccessScreenCallbacks): AccessScreen {
  let rows: AccessRow[] = [];
  let statusFilter: StatusFilter = "all";
  let ipFilter = "";
  let errorMsg: string | null = null;

  const body = el("div", { class: "editor access-screen" });
  const root = el("div", { class: "items-layout access-layout" }, body);

  function emitRefresh(): void {
    const filter: { status?: "pending" | "approved"; ip?: string } = {};
    if (statusFilter !== "all") filter.status = statusFilter;
    if (ipFilter.trim()) filter.ip = ipFilter.trim();
    cb.onRefresh(filter);
  }

  function render(): void {
    body.replaceChildren();

    body.append(
      el("h2", {}, "Access requests"),
      el(
        "p",
        { class: "access-hint" },
        "Players whose email is not yet approved land here when they request a login code. Approve to whitelist them (they'll need to click \"Receive code\" again); reject to drop the request. Use the IP filter + bulk-reject if you spot a flood from the same source.",
      ),
    );

    // Add-email form -------------------------------------------------------
    const addInput = el("input", {
      type: "email",
      placeholder: "new-tester@example.com",
      class: "access-add-input",
    }) as HTMLInputElement;
    const addBtn = el(
      "button",
      {
        class: "button publish",
        onclick: () => {
          const v = addInput.value.trim();
          if (!v) return;
          cb.onAdd(v);
          addInput.value = "";
        },
      },
      "Add as approved",
    );
    body.append(
      el(
        "div",
        { class: "access-add-row" },
        el("label", {}, "Pré-approver un email :"),
        addInput,
        addBtn,
      ),
    );

    // Filter row -----------------------------------------------------------
    const statusSel = el(
      "select",
      {
        class: "access-filter-status",
        onchange: (e: Event) => {
          statusFilter = (e.target as HTMLSelectElement).value as StatusFilter;
          emitRefresh();
        },
      },
      el("option", { value: "all", ...(statusFilter === "all" ? { selected: "" } : {}) }, "All"),
      el("option", { value: "pending", ...(statusFilter === "pending" ? { selected: "" } : {}) }, "Pending"),
      el("option", { value: "approved", ...(statusFilter === "approved" ? { selected: "" } : {}) }, "Approved"),
    ) as HTMLSelectElement;

    const ipInput = el("input", {
      type: "text",
      placeholder: "IP (exact match)",
      value: ipFilter,
      class: "access-filter-ip",
      oninput: (e: Event) => {
        ipFilter = (e.target as HTMLInputElement).value;
      },
    }) as HTMLInputElement;

    const ipApplyBtn = el(
      "button",
      { class: "button secondary", onclick: () => emitRefresh() },
      "Apply filters",
    );

    const bulkBtn = el(
      "button",
      {
        class: "button danger",
        onclick: () => {
          const ip = ipFilter.trim();
          if (!ip) return;
          if (!confirm(`Rejeter toutes les demandes 'pending' pour l'IP ${ip} ?`)) return;
          cb.onBulkRejectByIp(ip);
        },
      },
      "Bulk reject this IP",
    );

    body.append(
      el(
        "div",
        { class: "access-filter-row" },
        el("label", {}, "Status:"),
        statusSel,
        el("label", {}, "IP:"),
        ipInput,
        ipApplyBtn,
        bulkBtn,
      ),
    );

    if (errorMsg) {
      body.append(
        el(
          "div",
          { class: "access-error" },
          `Erreur : ${errorMsg}`,
        ),
      );
    }

    // Table ----------------------------------------------------------------
    if (rows.length === 0) {
      body.append(el("p", { class: "access-empty" }, "Aucune demande à afficher."));
      return;
    }

    const now = Date.now();
    const table = el(
      "table",
      { class: "access-table" },
      el(
        "thead",
        {},
        el(
          "tr",
          {},
          el("th", {}, "Email"),
          el("th", {}, "Status"),
          el("th", {}, "Source"),
          el("th", {}, "IP"),
          el("th", {}, "Count"),
          el("th", {}, "First"),
          el("th", {}, "Last"),
          el("th", {}, "Actions"),
        ),
      ),
    );

    const tbody = el("tbody", {});
    for (const r of rows) {
      const actions = el("td", { class: "access-actions" });
      if (r.status === "pending") {
        actions.append(
          el(
            "button",
            {
              class: "button publish",
              onclick: () => cb.onApprove(r.email),
            },
            "Approve",
          ),
          el(
            "button",
            {
              class: "button danger",
              onclick: () => cb.onReject(r.email),
            },
            "Reject",
          ),
        );
      } else {
        actions.append(el("span", { class: "access-approved-tag" }, "✓ approved"));
      }

      const ipCell = el("td", {});
      if (r.ip) {
        ipCell.append(
          el(
            "button",
            {
              class: "access-ip-link",
              title: "Filter by this IP",
              onclick: () => {
                ipFilter = r.ip!;
                emitRefresh();
              },
            },
            r.ip,
          ),
        );
      } else {
        ipCell.textContent = "—";
      }

      tbody.append(
        el(
          "tr",
          { class: `access-row-${r.status}` },
          el("td", { class: "access-email" }, r.email),
          el(
            "td",
            {},
            el(
              "span",
              { class: `access-badge access-badge-${r.status}` },
              r.status,
            ),
          ),
          el(
            "td",
            {},
            el(
              "span",
              { class: "access-source" },
              r.source === "admin_added" ? "admin" : "request",
            ),
          ),
          ipCell,
          el("td", { class: "access-count" }, String(r.requestCount)),
          el("td", {}, formatRelative(now - r.firstRequestAt)),
          el("td", {}, formatRelative(now - r.lastRequestAt)),
          actions,
        ),
      );
    }
    table.append(tbody);
    body.append(table);
  }

  render();

  return {
    root,
    setRows(next: AccessRow[]): void {
      rows = next;
      errorMsg = null;
      render();
    },
    setError(reason: string | null): void {
      errorMsg = reason;
      render();
    },
  };
}
